import { z } from "zod";
import {
  ContactAddress,
  Currency,
  Gender,
  Identification,
  PersonName,
  PhoneNumber,
  TradeAbility,
  UpdatePersonName,
} from "../common-models";

export const CustomerBase = z.object({
  affiliate_token: z.string().nullable(),
  birth_date: z.string().nullish(),
  category_id: z.number(),
  gender: Gender,
  /**
   * External customer ID (membership number).
   *
   * This field is populated for Shoprite customers who receive a membership number,
   * but is null for Tourvest customers who don't use the membership system.
   *
   * @example
   * // Shoprite customer
   * { external_id: "MGB279668805" }
   *
   * // Tourvest customer
   * { external_id: null }
   */
  external_id: z.string().nullable(),
  email: z.string().nullable(),
  name: PersonName,
  mobile: PhoneNumber,
  physical_address: ContactAddress,
  postal_address: ContactAddress,
  identification: Identification,
  nationality_id: z.number().nullable(),
  source_of_funds_id: z.number().nullish(),
  occupation_id: z.number().nullable(),
});
export type CustomerBase = z.infer<typeof CustomerBase>;
export const CustomerStatus = z.enum([
  "new",
  "pending",
  "active",
  "rejected",
  "closed",
  "suspended",
  "pending_account_details",
]);
export type CustomerStatus = z.infer<typeof CustomerStatus>;
export const Customer = CustomerBase.extend({
  id: z.number(),
  status: CustomerStatus,
  created_at: z.string(),
  updated_at: z.string(),
});
export type Customer = z.infer<typeof Customer>;

export const CreateCustomerRequest = z.object({
  otp: z.string(),
  terms_and_conditions_accepted: z.boolean(),
  password: z.string(),
  affiliate_token: z.string().optional(),
  birth_date: z.string().optional(),
  category_id: z.number().optional(),
  gender: Gender,
  email: z.string().optional(),
  name: PersonName,
  mobile: PhoneNumber,
  physical_address: ContactAddress.optional(),
  postal_address: ContactAddress.optional(),
  identification: Identification.optional(),
  nationality_id: z.number().optional(),
  source_of_funds_id: z.number().optional(),
  occupation_id: z.number().optional(),
  marketing_accepted: z.boolean().optional(),
});
export type CreateCustomerRequest = z.infer<typeof CreateCustomerRequest>;

export const CreateCustomerResponse = z.object({ customer: Customer });
export type CreateCustomerResponse = z.infer<typeof CreateCustomerResponse>;

export const UpdateCustomer = z.object({
  affiliate_token: z.string().nullish(),
  birth_date: z.string().nullish(),
  category_id: z.number().nullish(),
  gender: Gender.nullish(),
  email: z.string().nullish(),
  name: UpdatePersonName.optional(),
  mobile: PhoneNumber.nullish(),
  physical_address: ContactAddress.optional(),
  postal_address: ContactAddress.optional(),
  identification: Identification.optional(),
  nationality_id: z.number().nullish(),
  source_of_funds_id: z.number().nullish(),
  occupation_id: z.number().nullish(),
  password: z.string().nullish(),
});
export type UpdateCustomer = z.infer<typeof UpdateCustomer>;

export const TradeLimit = z.object({
  name: z.string(),
  label: z.string(),
  currency: Currency,
  trading_abilities: z.array(TradeAbility),
  limit: z.number().nullable(),
  consumed: z.coerce.number().nullable(),
  remaining: z.coerce.number().nullable(),
});
export type TradeLimit = z.infer<typeof TradeLimit>;

export const CustomerLimitsResponse = z.object({ limits: z.array(TradeLimit) });
export type CustomerLimitsResponse = z.infer<typeof CustomerLimitsResponse>;

export const GetCustomerResponse = z.object({ customer: Customer });
export type GetCustomerResponse = z.infer<typeof GetCustomerResponse>;

export const UpdateCustomerResponse = z.object({ customer: Customer });
export type UpdateCustomerResponse = z.infer<typeof UpdateCustomerResponse>;

export const GetCustomerSignUpStatusResponse = z.object({ status: z.string() });
export type GetCustomerSignUpStatusResponse = z.infer<
  typeof GetCustomerSignUpStatusResponse
>;

export const VerifyRegistrationIdResponse = z.unknown();
type VerifyRegistrationIdResponse = z.infer<
  typeof VerifyRegistrationIdResponse
>;
